import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { formatKm, formatPrice } from "../lib/format";
import { Calendar, Gauge, Fuel, Settings2, Car, Phone, Mail, ArrowLeft, Shield, BadgeCheck, FileCheck, Sparkles, Send, Camera, Zap, Wrench, FileText, ExternalLink, Palette, Armchair } from "lucide-react";
import CarCard from "../components/CarCard";
import { carsApi, inquiriesApi, imageUrl, PLACEHOLDER_IMAGE } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";
import Lightbox, { GalleryGrid } from "../components/Lightbox";
import ErrorBoundary from "../components/ErrorBoundary";
import WhatsAppIcon from "../components/WhatsAppIcon";
import { COMPANY } from "../mock/mock";
import { translateOption, translateCategory, OPTION_CATEGORIES } from "../lib/carOptions";
import { useI18n } from "../i18n/I18nContext";

export default function CarDetail() {
  const { id } = useParams();
  const { toast } = useToast();
  const { t, lang } = useI18n();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setLoading(true);
    carsApi.get(id)
      .then(async (c) => {
        setCar(c);
        const all = await carsApi.list({ brand: c.brand, light: 1 });
        setRelated(all.filter((x) => x.id !== c.id).slice(0, 4));
      })
      .catch(() => setCar(null))
      .finally(() => setLoading(false));
  }, [id]);

  const openLightbox = (i) => {
    setLightboxIndex(i);
    setLightboxOpen(true);
  };

  const submitInquiry = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await inquiriesApi.create({
        type: "car",
        ...form,
        subject: `Vraag over ${car.title}`,
        car_id: car.id,
      });
      toast({ title: "Bericht verzonden", description: "Wij nemen zo snel mogelijk contact met u op." });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast({ title: "Versturen mislukt", description: "Probeer opnieuw of bel ons." });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#1a1a1a] min-h-screen text-white">
        <Navbar />
        <div className="pt-[140px] text-center text-white/40">Laden...</div>
      </div>
    );
  }
  if (!car) {
    return (
      <div className="bg-[#1a1a1a] min-h-screen text-white">
        <Navbar />
        <div className="pt-[140px] px-5 text-center">
          <h1 className="text-3xl text-white mb-3">{t("car.notFound")}</h1>
          <Link to="/stock" className="text-[#c5a76f]">← {t("car.backToStock")}</Link>
        </div>
      </div>
    );
  }

  const images = car.images?.length ? car.images : [];

  return (
    <div className="bg-[#1a1a1a] text-white min-h-screen">
      <Navbar />
      <Toaster />
      <ErrorBoundary>
        <Lightbox images={images} startIndex={lightboxIndex} open={lightboxOpen} onClose={() => setLightboxOpen(false)} />
      </ErrorBoundary>

      <section className="pt-[120px] pb-6 px-5 lg:px-8">
        <div className="max-w-[1400px] mx-auto">
          <Link to="/stock" className="inline-flex items-center gap-2 text-white/60 hover:text-[#c5a76f] text-sm">
            <ArrowLeft className="w-4 h-4" /> {t("car.backToStock")}
          </Link>
        </div>
      </section>

      {/* Gallery */}
      <section className="px-5 lg:px-8 pb-10">
        <div className="max-w-[1400px] mx-auto">
          {images.length > 0 ? (
            <div className="relative">
              <GalleryGrid images={images} onOpen={openLightbox} />
              <button
                onClick={() => openLightbox(0)}
                className="absolute bottom-4 right-4 inline-flex items-center gap-2 px-4 py-2.5 bg-black/80 hover:bg-[#c5a76f] hover:text-black backdrop-blur text-white text-xs font-semibold tracking-widest uppercase rounded transition-colors"
              >
                <Camera className="w-4 h-4" /> {t("car.showAllPhotos").replace("{n}", images.length)}
              </button>
              {car.is_upcoming ? (
                <div className="absolute top-4 left-0 bg-[#3b82f6] text-white font-bold text-xs tracking-widest px-6 py-1.5">{t("stock.upcoming")}</div>
              ) : car.reserved ? (
                <div className="absolute top-4 left-0 bg-[#ef4444] text-white font-bold text-xs tracking-widest px-6 py-1.5">{t("stock.reserved")}</div>
              ) : car.ready ? (
                <div className="absolute top-4 left-0 bg-[#c5a76f] text-black font-bold text-xs tracking-widest px-6 py-1.5">{t("stock.ready")}</div>
              ) : null}
            </div>
          ) : (
            <div className="relative aspect-[16/8] bg-[#242424] rounded-md overflow-hidden">
              <img
                src={PLACEHOLDER_IMAGE}
                alt={`${car.brand || ""} ${car.model || ""} — foto's volgen binnenkort`.trim()}
                className="w-full h-full object-cover"
              />
              {car.is_upcoming ? (
                <div className="absolute top-4 left-0 bg-[#3b82f6] text-white font-bold text-xs tracking-widest px-6 py-1.5">{t("stock.upcoming")}</div>
              ) : null}
            </div>
          )}
        </div>
      </section>

      <section className="px-5 lg:px-8 pb-16">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="mt-2">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: "'Familjen Grotesk', serif" }}>{car.title}</h1>
              <div className="text-[#c5a76f] text-2xl font-bold mb-8">{formatPrice(car.price)}</div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
                {[
                  { icon: Calendar, label: t("car.year"), val: car.year },
                  car.first_registration ? { icon: FileCheck, label: t("car.firstReg"), val: car.first_registration } : null,
                  { icon: Gauge, label: t("car.km"), val: formatKm(car.km) },
                  { icon: Fuel, label: t("car.fuel"), val: car.fuel },
                  { icon: Settings2, label: t("car.transmission"), val: car.transmission },
                  { icon: Car, label: t("car.bodyType"), val: car.body },
                  { icon: BadgeCheck, label: t("car.brand"), val: car.brand },
                  { icon: FileCheck, label: t("car.model"), val: car.model || "—" },
                  car.color_exterior ? { icon: Palette, label: t("car.colorExt"), val: car.color_exterior } : null,
                  car.color_interior ? { icon: Armchair, label: t("car.colorInt"), val: car.color_interior } : null,
                  car.horsepower ? { icon: Zap, label: t("car.horsepower"), val: `${car.horsepower} ${t("car.hp")}` } : null,
                  car.engine_size ? { icon: Wrench, label: t("car.engineSize"), val: `${car.engine_size} ${t("car.cc")}` } : null,
                  { icon: Shield, label: t("car.warranty"), val: t("car.months12") },
                ].filter(Boolean).map((s) => (
                  <div key={s.label} className="p-4 bg-[#242424] border border-white/5 rounded">
                    <s.icon className="w-4 h-4 text-[#c5a76f] mb-2" />
                    <div className="text-[11px] text-white/50 uppercase tracking-wider">{s.label}</div>
                    <div className="text-white text-sm font-medium mt-1">{s.val}</div>
                  </div>
                ))}
              </div>

              {car.description && (
                <div className="mb-12" data-testid="car-description">
                  <h2 className="text-2xl font-bold text-white mb-5" style={{ fontFamily: "'Familjen Grotesk', serif" }}>{t("car.description")}</h2>
                  <DescriptionBlock text={pickLangSection((lang === "fr" && car.description_fr) || (lang === "en" && car.description_en) || car.description, lang)} />
                </div>
              )}

              {car.options?.length > 0 && (
                <div className="mb-12" data-testid="car-options">
                  <h2 className="text-2xl font-bold text-white mb-5" style={{ fontFamily: "'Familjen Grotesk', serif" }}>{t("car.optionsHeader")}</h2>
                  <OptionsGrouped options={car.options} translations={car.options_translations} lang={lang} />
                </div>
              )}

              {/* Photo strip - all photos */}
              {images.length > 1 && (
                <div className="mt-12">
                  <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: "'Familjen Grotesk', serif" }}>{t("car.allPhotos")} ({images.length})</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {images.map((src, i) => (
                      <button
                        key={i}
                        onClick={() => openLightbox(i)}
                        className="group relative aspect-[4/3] rounded overflow-hidden bg-[#242424]"
                      >
                        <img src={imageUrl(src)} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <aside className="lg:sticky lg:top-[110px] h-fit space-y-5">
            <div className="bg-[#242424] border border-[#c5a76f]/30 rounded-md p-7">
              <div className="text-sm text-white/60 mb-1">{t("car.price")}</div>
              <div className="text-3xl font-bold text-[#c5a76f] mb-3">{formatPrice(car.price)}</div>
              {car.vat_deductible && (
                <div
                  data-testid="car-vat-deductible-badge"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-5 bg-[#c5a76f]/15 border border-[#c5a76f]/40 text-[#c5a76f] text-[11px] font-bold tracking-wider uppercase rounded"
                >
                  <BadgeCheck className="w-3.5 h-3.5" /> {t("car.vatDeductible")}
                </div>
              )}
              <div className="space-y-3">
                <a href="tel:+32471866784" className="flex items-center justify-center gap-2 w-full px-5 py-3.5 bg-[#c5a76f] text-black font-medium hover:bg-[#d8bd86] transition-colors text-sm tracking-wide uppercase rounded">
                  <Phone className="w-4 h-4" /> {t("common.callUs")}
                </a>
                <a href="mailto:info@tr-premiumcars.be" className="flex items-center justify-center gap-2 w-full px-5 py-3.5 border border-[#c5a76f] text-[#c5a76f] hover:bg-[#c5a76f] hover:text-black transition-colors text-sm tracking-wide uppercase rounded">
                  <Mail className="w-4 h-4" /> {t("car.sendEmail")}
                </a>
                <a
                  href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(`Hallo, ik heb een vraag over ${car.title} (${window.location.href})`)}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="car-whatsapp-button"
                  className="flex items-center justify-center gap-2 w-full px-5 py-3.5 bg-[#25D366] text-white font-medium hover:bg-[#1DA851] transition-colors text-sm tracking-wide uppercase rounded"
                >
                  <WhatsAppIcon className="w-4 h-4" /> WhatsApp
                </a>
                <Link to="/financiering" className="block text-center w-full px-5 py-3.5 border border-white/15 text-white hover:border-[#c5a76f] hover:text-[#c5a76f] transition-colors text-sm tracking-wide uppercase rounded">
                  {t("nav.financing")}
                </Link>
                <a
                  href={`https://klantenportaal.financo.be/customer?utm_source=trprem&utm_medium=listing&utm_content=${encodeURIComponent(car.id || "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="car-financo-link"
                  className="flex items-center justify-center gap-1.5 text-[11px] tracking-wide text-white/45 hover:text-[#c5a76f] transition-colors pt-1"
                >
                  {t("car.financoCta")}
                  <ExternalLink className="w-3 h-3" />
                </a>
                {car.carpass_url && (
                  <a
                    href={car.carpass_url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center gap-2 w-full px-5 py-3.5 bg-[#1a1a1a] border border-[#c5a76f]/40 text-[#c5a76f] hover:bg-[#c5a76f]/10 transition-colors text-sm tracking-wide uppercase rounded"
                  >
                    <FileText className="w-4 h-4" /> {t("car.carPassLink")} <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
              <div className="mt-7 pt-7 border-t border-white/10 space-y-4 text-sm">
                <div className="flex items-start gap-3 text-white/80"><Shield className="w-4 h-4 text-[#c5a76f] mt-0.5" /> {t("car.legalWarranty")}</div>
                <div className="flex items-start gap-3 text-white/80"><Sparkles className="w-4 h-4 text-[#c5a76f] mt-0.5" /> {t("car.cleaning")}</div>
                <div className="flex items-start gap-3 text-white/80"><FileCheck className="w-4 h-4 text-[#c5a76f] mt-0.5" /> {t("car.carPass")}</div>
              </div>
            </div>

            <form onSubmit={submitInquiry} className="bg-[#242424] border border-white/5 rounded-md p-6 space-y-3">
              <div className="text-white font-semibold mb-1">{t("car.askAboutThisCar")}</div>
              <input required placeholder={t("common.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-3 py-2.5 text-white text-sm" />
              <input required type="email" placeholder="E-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-3 py-2.5 text-white text-sm" />
              <input placeholder={t("common.phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-3 py-2.5 text-white text-sm" />
              <textarea required rows="3" placeholder="Uw bericht..." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-3 py-2.5 text-white text-sm" />
              <button disabled={sending} className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#c5a76f] text-black hover:bg-[#d8bd86] transition-colors text-xs font-semibold tracking-widest uppercase disabled:opacity-60 rounded">
                {sending ? t("common.sending") : t("car.sendInquiry")} <Send className="w-3.5 h-3.5" />
              </button>
            </form>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="px-5 lg:px-8 pb-24">
          <div className="max-w-[1400px] mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-8" style={{ fontFamily: "'Familjen Grotesk', serif" }}>{t("car.relatedCars")}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map((c) => <CarCard key={c.id} car={c} />)}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}

// ---------- Description renderer ----------
// Splits text into paragraphs on blank lines, collapses single line breaks
// inside a paragraph to a space so AS24-style hard wraps don't create awkward
// uneven spacing. Bullet-style lines (- ..., • ...) are detected and rendered
// as a list. HTML entities (&amp; &lt; &#x27; ...) are decoded so AS24 imports
// render naturally.
function decodeEntities(s) {
  if (!s) return s;
  return s
    .replace(/&#x([0-9a-fA-F]+);/g, (_, h) => String.fromCodePoint(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, d) => String.fromCodePoint(parseInt(d, 10)))
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, " ");
}

// Bilingual AS24 texts ("🇳🇱 ... 🇫🇷 ...") → keep only the visitor's language (default NL)
function pickLangSection(text, lang) {
  if (!text || !text.includes("🇳🇱") || !text.includes("🇫🇷")) return text;
  const parts = {};
  text.split(/(🇳🇱|🇫🇷|🇬🇧)/).reduce((cur, chunk) => {
    if (chunk === "🇳🇱") return "nl";
    if (chunk === "🇫🇷") return "fr";
    if (chunk === "🇬🇧") return "en";
    if (cur) parts[cur] = ((parts[cur] || "") + chunk).trim();
    return cur;
  }, null);
  return (lang === "fr" && parts.fr) || (lang === "en" && parts.en) || parts.nl || text;
}

function DescriptionBlock({ text }) {
  if (!text) return null;
  // Decode entities, strip stray HTML tags, normalise CR, split on blank lines
  const cleaned = decodeEntities(text).replace(/<[^>]+>/g, "").replace(/\r\n?/g, "\n");
  const paragraphs = cleaned
    .split(/\n\s*\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <div className="space-y-4 text-white/80 leading-relaxed text-[15px]" data-testid="car-description-body">
      {paragraphs.map((para, i) => {
        const lines = para.split("\n").map((l) => l.trim()).filter(Boolean);
        const isBullet = (l) => /^[-•·*–]\s+/.test(l);
        const isList = lines.length > 1 && lines.every(isBullet);
        const isSection = lines.length > 1 && !isBullet(lines[0]) && lines[0].length < 60 && lines.slice(1).every(isBullet);
        if (isSection) {
          return (
            <div key={i}>
              <h3 className="text-white font-semibold mb-2">{lines[0]}</h3>
              <ul className="list-disc pl-5 space-y-1.5 marker:text-[#c5a76f]">
                {lines.slice(1).map((l, j) => (
                  <li key={j}>{l.replace(/^[-•·*–]\s+/, "")}</li>
                ))}
              </ul>
            </div>
          );
        }
        if (isList) {
          return (
            <ul key={i} className="list-disc pl-5 space-y-1.5 marker:text-[#c5a76f]">
              {lines.map((l, j) => (
                <li key={j}>{l.replace(/^[-•·*–]\s+/, "")}</li>
              ))}
            </ul>
          );
        }
        // Single-line breaks inside a paragraph → soft wrap
        return (
          <p key={i}>
            {lines.map((l, j) => (
              <React.Fragment key={j}>
                {l}
                {j < lines.length - 1 && " "}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}

// ---------- Options grouped by category ----------
function OptionsGrouped({ options, translations, lang }) {
  // Build a map: category label → matched items in this car
  const grouped = OPTION_CATEGORIES.map((cat) => ({
    label: cat.label,
    items: cat.items.filter((i) => options.includes(i)),
  })).filter((g) => g.items.length > 0);

  // Anything not part of the canonical catalog → "Overige opties"
  const known = new Set(OPTION_CATEGORIES.flatMap((c) => c.items));
  const extras = options.filter((o) => !known.has(o));
  if (extras.length) {
    grouped.push({ label: "Overige uitrusting", items: extras });
  }

  return (
    <div className="space-y-7" data-testid="car-options-grouped">
      {grouped.map((g) => (
        <div key={g.label}>
          <div className="flex items-center gap-3 mb-3">
            <div className="h-px flex-1 bg-gradient-to-r from-[#c5a76f]/40 to-transparent" />
            <span className="text-[#c5a76f] text-xs tracking-[0.25em] uppercase font-semibold whitespace-nowrap">
              {translateCategory(g.label, lang)}
            </span>
            <div className="h-px flex-1 bg-gradient-to-l from-[#c5a76f]/40 to-transparent" />
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
            {g.items.map((o) => (
              <li key={o} className="flex items-start gap-2 text-white/80 text-sm leading-snug">
                <BadgeCheck className="w-4 h-4 text-[#c5a76f] flex-shrink-0 mt-0.5" />
                <span>{translateOption(o, lang, translations)}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
